import * as React from 'react';
import styled, { css } from 'styled-components/macro';
import { theme } from 'lib/style';
import { Link, NavLink } from 'react-router-dom';
import { HiChevronLeft } from 'react-icons/hi';
import { useAuth } from '../context';
import { useLandingPageBuilderContext } from '../../app/pages/design/landingPageBuilder/context';
import ProductInfoTooltip from 'lib/components/ProductInfoTooltip/ProductInfoTooltip';
import { isAdminSession } from 'lib/utils/auth';
import { InviteFriendsModal } from './modal/ModalInviteFriends';
import { Button } from 'react-covideo-common';
import { IoMdPeople } from 'react-icons/io';
import { checkIfFreemium } from 'lib/utils/functions';
import { PackageNameLabel } from 'lib/utils/productFeature';
import { useTranslation } from 'react-i18next';

import { CommonTypography } from './styles/typography';
import { PackageName } from 'lib/const/PackageAdditionalInfo';
import { WHITELABEL_NAME } from 'lib/api/whitelabel/getWhiteLabel';
import { AccessRole } from 'lib/const';
import { useCDK } from 'lib/hooks/useCdk';

type ContainerProps = {
  padding?: number;
  overflow?: string;
};

const Container = styled.nav<ContainerProps>`
  z-index: 6;
  display: none;
  justify-content: flex-start;
  flex-direction: column;
  height: 89vh;
  position: fixed;
  left: 0;
  top: 0;
  .disabled {
    cursor: not-allowed;
    pointer-events: all !important;
    color: rgba(255, 255, 255, 0.5);
  }
  padding-top: ${props => props.padding || 100}px;
  min-width: 225px;
  background-color: ${theme.palette.covideoBlue02NonAlpha};
  ${theme.mediaQueryMinWidth.mb} {
    display: flex;
  }
  overflow-y: ${props => props.overflow || 'visible'};
`;

type NavItemProps = {
  disabled?: boolean;
};

const NavItem = styled(NavLink)<NavItemProps>`
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.71;
  display: flex;
  padding: 10px 16px 10px 28px;
  justify-content: flex-start;
  min-height: 20px;
  height: 40px;
  align-items: center;
  text-align: center; /* to center icons */
  opacity: 1;
  transition: all 0.2s;
  ${theme.fontNormal500};
  font-size: ${theme.fontSizes.md};
  color: ${theme.palette.primaryDarkBlue75Percent};
  text-decoration: none;

  border-left: 2px solid transparent;
  > svg {
    color: rgba(0, 27, 83, 0.4);
    fill: rgba(0, 27, 83, 0.4);
  }

  g > path {
    fill: rgba(0, 27, 83, 0.4);
  }
  .notificationCount {
    background-color: ${theme.palette.primaryDarkBlue50Percent};
  }

  .snippets {
    color: rgba(0, 27, 83, 0.4);
    fill: rgba(0, 27, 83, 0.4);
  }

  ${props =>
    !props.disabled &&
    `
    &:hover {
      color: ${theme.palette.primaryDarkBlue};
      background-color: ${theme.palette.white};
    }
  `}

  &.active {
    background-color: ${theme.palette.white};
    color: ${theme.palette.primaryDarkBlue};
    border-left: 2px solid ${theme.palette.primaryDarkBlue};
    svg {
      color: ${theme.palette.primaryDarkBlue};
      fill: ${theme.palette.primaryDarkBlue};
    }

    // This is for AI icons which are SVG imported as ReactComponent
    svg.ai path {
      fill: ${theme.palette.primaryDarkBlue};
    }

    g > path {
      fill: ${theme.palette.primaryDarkBlue};
    }
    .notificationCount {
      background-color: ${theme.palette.primaryDarkBlue};
    }
  }
`;

const Icon = styled.span<{ color?: string }>`
  margin: 0;
  font-size: ${theme.iconSizeS};
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  color: ${props => (props.color ? props.color : 'rgba(0, 27, 83, 0.4)')};
`;

const IconWrap = styled.div<{
  logoutIconColor?: string;
  logoutTitleColor?: string;
}>`
  position: relative;

  .logout {
    .icon {
      color: ${props => props.logoutIconColor} !important;
    }
    .title {
      color: ${props => props.logoutTitleColor} !important;
    }
  }
`;

const Title = styled.span<{ hasNotifications?: any; color?: string }>`
  ${theme.fontNormal600}
  // margin-left: 14px;
  text-align: left;
  font-size: 15px;
  letter-spacing: 0;
  color: ${props =>
    props.color ? props.color : theme.palette.primaryDarkBlue};
  position: relative;

  ${({ hasNotifications, theme }) =>
    hasNotifications &&
    css`
      :after {
        content: '';
        width: 6px;
        height: 6px;
        background: ${theme.colors.primary[100]};
        position: absolute;
        border-radius: 6px;
        left: -18px;
        top: 2px;
      }
    `}

  ${theme.mediaQueryMinWidth.lg} {
    display: block;
  }
`;

const BackButton = styled(Link)`
  border: none;
  text-decoration: none;
  box-shadow: none;
  background: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.6;
  padding: 10px 16px 10px 32px;
  color: ${theme.palette.primaryDarkBlue};
  font-weight: 500;
  min-width: 160px;
  text-decoration: underline;
  &:hover {
    color: ${theme.palette.primaryDarkBlue};
    background-color: ${theme.palette.white};
  }
  ${theme.mediaQueryMinWidth.mb} {
    aside {
      display: none;
    }
  }
  ${theme.mediaQueryMinWidth.sm} {
    width: 100px;
    aside {
      display: block;
    }
    img {
      margin: 0 16px 0 0;
    }
  }
`;

const AdditionalIcon = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: ${theme.palette.secondaryGrey};
`;

const AdditionalIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  background-color: #e6e6e6;
  border-radius: 6px;
  position: absolute;
  right: -10px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const Badge = styled.span`
  font-size: 10px;
  padding: 2px 4px;
  margin-left: 4px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.primary[100]}; /** SUS-961 changes **/
`;

const Notification = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #001b53;
  border-radius: 4px;
  color: #ffffff;
  font-weight: 600;
  font-size: 12px;
  width: 24px;
  height: 24px;
`;

const TooltipMain = styled.div`
  width: 100%;
`;

const EnabledItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
`;

const EnabledDataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const DisabledItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  opacity: 0.5;
`;

const DisabledDataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 7px 16px 0px 28px;
  gap: 12px;
`;

const Label = styled(CommonTypography).attrs({ as: 'p' })`
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  color: ${theme.palette.covideoBlue40};
`;

const Line = styled.div`
  flex: 1;
  width: 100%;
  height: 1px;
  background-color: ${theme.palette.blue05};
`;

const ItemsWrapper = styled.div<{ paddingBottom?: string }>`
  padding-bottom: ${props => props.paddingBottom || '0px'};
  text-align: center;
`;

const getNextPlanForSidebarItems = (
  title: string,
  t: (key: string) => string
) => {
  return [
    t('Video Attributes'),
    t('Files'),
    t('Boards'),
    t('Captions'),
    t('Security'),
    t('Schedules'),
    t('Templates'),
    t('Appearance'),
    t('Annotations'),
    t('Overlay URLs'),
    t('QR Codes'),
    t('Video Expiration'),
    t('Lives'),
    t('Social Profiles'),
  ].includes(title)
    ? PackageNameLabel.TEAMS
    : PackageNameLabel.PRO;
};

const getTootipTextByTitleForStandardUsers = (
  title: string,
  t: (key: string) => string
) => {
  const defaultText = t('Please contact your Covideo Admin to request access.');
  const tooltipText: { [key: string]: string } = {
    [t('Files')]: t(
      'Files feature is not enabled for your account. Please contact your Covideo admin to request access.'
    ),
    [t('Boards')]: t(
      'Boards feature is not enabled for your account. Please contact your Covideo admin to request access.'
    ),
    [t('Captions')]: t(
      "Captions aren't enabled for your account. Please contact your Admin to obtain the necessary license."
    ),
  };

  return tooltipText[title] || defaultText;
};
export interface SidebarItem {
  productFeatureId?: number;
  title: string;
  path?: string;
  icon?: any;
  exact?: boolean;
  additionalIcon?: any;
  badge?: string;
  notificationCount?: string;
  isDisabled?: boolean;
  isPaid?: boolean;
  paidUrl?: string;
  isLabel?: boolean;
  iconColor?: string;
  color?: string;
  className?: string;
  key?: string;
}

interface Props {
  hasBack?: boolean;
  items: SidebarItem[];
  removeBackButton?: boolean;
}

//BACK ICON
const BackIcon = ({ hasBack }: Pick<Props, 'hasBack'>) => {
  const { t } = useTranslation();
  if (hasBack) {
    return (
      <IconWrap>
        <BackButton to={'/'}>
          <HiChevronLeft
            color={theme.palette.covideoBlue100}
            width={16}
            height={16}
          />
          <aside>{t('Back')}</aside>
        </BackButton>
      </IconWrap>
    );
  }
  return null;
};

const DisabledItemComponent = ({ item }: { item: SidebarItem }) => {
  const { title, icon, additionalIcon, badge, notificationCount } = item;
  return (
    <DisabledItemWrapper>
      <Icon>{icon}</Icon>
      <DisabledDataWrapper>
        <Title>
          {title}
          {!!badge && <Badge>{badge}</Badge>}
          {Number(notificationCount) > 0 && (
            <Notification>{notificationCount}</Notification>
          )}
        </Title>
        {!!additionalIcon && (
          <AdditionalIconWrapper>
            <AdditionalIcon>{additionalIcon}</AdditionalIcon>
          </AdditionalIconWrapper>
        )}
      </DisabledDataWrapper>
    </DisabledItemWrapper>
  );
};
//DISABLED ITEM
const DisabledItem = ({
  item,
  hasUserLevelAccessForFeature,
}: {
  item: SidebarItem;
  hasUserLevelAccessForFeature: boolean;
}) => {
  const { t } = useTranslation();
  const { title, isDisabled, isPaid, paidUrl } = item;
  const nextPlan = getNextPlanForSidebarItems(title, t);

  const isNonFreemiumTooltip =
    !hasUserLevelAccessForFeature && !isDisabled ? true : false;
  return (
    <TooltipMain>
      <ProductInfoTooltip
        nextPlan={nextPlan}
        isPaid={isPaid}
        paidUrl={paidUrl}
        productFeatureId={item.productFeatureId}
        isNonFreemiumTooltip={isNonFreemiumTooltip}
        tooltipText={getTootipTextByTitleForStandardUsers(title, t)}
        style={{
          left: 'calc(100% + 150px)',
          ...(isNonFreemiumTooltip ? { opacity: 1 } : {}),
        }}
      >
        <DisabledItemComponent item={item} />
      </ProductInfoTooltip>
    </TooltipMain>
  );
};

//ENABLED ITEM
const EnabledItem = ({ item }: { item: SidebarItem }) => {
  const { t } = useTranslation();
  const { title, icon, badge, notificationCount } = item;
  const hasNotificationsCount = !!notificationCount;
  const shouldShowNotification =
    title !== t('Training') && hasNotificationsCount;
  return (
    <EnabledItemWrapper>
      <Icon color={item.iconColor} className='icon'>
        {icon}
      </Icon>
      <EnabledDataWrapper>
        <Title color={item.color} className='title'>
          {title}
          {!!badge && <Badge>{badge}</Badge>}
        </Title>
        {shouldShowNotification && (
          <Notification className={'notificationCount'}>
            {notificationCount}
          </Notification>
        )}
      </EnabledDataWrapper>
    </EnabledItemWrapper>
  );
};

//SIDEBAR NAVIGATION
export const SidebarNavigation = ({ items, hasBack = true }: Props) => {
  const { t } = useTranslation();
  const { userData, whitelabel } = useAuth();
  const {
    isCdkUser,
    logoutTitleColor,
    logoutIconColor,
    cdkNavItemTitlesToHide,
  } = useCDK();

  const { trialExpired, droplrAccess, customer, transcriptionAccess } =
    userData;
  const { isEnabled } = useLandingPageBuilderContext();
  const [inviteFriends, setInviteFriends] = React.useState(false);

  if (isEnabled) {
    return <></>;
  }

  const onNavItemClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    item: SidebarItem,
    hasUserLevelAccessForFeature: boolean = true
  ) => {
    const { isDisabled, title } = item;
    if (
      isDisabled ||
      trialExpired ||
      title === t('Help Center') ||
      !hasUserLevelAccessForFeature
    ) {
      e.preventDefault();
    }

    if (title === t('Help Center')) {
      window
        .open(whitelabel?.supportURL || 'https://support.covideo.com', '_blank')
        ?.focus();
    }
  };

  const isItemInvisible = (item: SidebarItem) => {
    const { title, key } = item;

    if (
      (cdkNavItemTitlesToHide.includes(title) ||
        cdkNavItemTitlesToHide.includes(key || '')) &&
      isCdkUser
    ) {
      return true;
    }

    if (title === t('Help Center') && !whitelabel.supportURL) {
      return true;
    }

    if (title === t('Billing') && whitelabel.name !== WHITELABEL_NAME.COVIDEO) {
      return true;
    }
    return false;
  };

  const setModal = (isOpen: boolean) => {
    setInviteFriends(isOpen);
  };

  const hasItemAccess = (item: SidebarItem) => {
    const { title } = item;
    const isAdmin = userData.access === AccessRole.ADMIN;
    if (isAdmin || whitelabel.name !== WHITELABEL_NAME.COVIDEO) {
      return true;
    }
    if (title === t('Files') || title === t('Boards')) {
      return !!droplrAccess && !!customer.droplrEnabled;
    }

    if (title === t('Captions')) {
      return !!transcriptionAccess && !!customer.transcriptions;
    }

    return true;
  };

  const isLegacyPackage =
    parseInt(userData.customer.packageId.toString(), 10) === PackageName.LEGACY;
  const isDroplrOrCaptionsCase = items.some(
    item =>
      item.title === t('Files') ||
      item.title === t('Boards') ||
      item.title === t('Captions')
  );
  const isUserAccessTooltipRequired =
    isDroplrOrCaptionsCase && (!droplrAccess || !transcriptionAccess);
  return (
    <Container
      padding={isAdminSession() || userData.trialAccount ? 136 : 96}
      overflow={
        isLegacyPackage && !isUserAccessTooltipRequired ? 'auto' : 'visible'
      }
    >
      <BackIcon hasBack={hasBack} />
      <ItemsWrapper
        paddingBottom={
          isLegacyPackage || isUserAccessTooltipRequired ? '65px' : '0px'
        }
      >
        {items.map((item: SidebarItem, index) => {
          const { path, exact, isDisabled, className } = item;

          const isInvisible = isItemInvisible(item);
          const hasUserLevelAccessForFeature = hasItemAccess(item);
          if (isInvisible) {
            return <React.Fragment key={index} />;
          }

          if (!!item.isLabel) {
            return (
              <LabelContainer key={index}>
                <Label>{item.title}</Label>
                <Line />
              </LabelContainer>
            );
          }
          const isNavItemDisabled =
            isDisabled || trialExpired || !hasUserLevelAccessForFeature;
          return (
            <IconWrap
              key={index}
              logoutIconColor={logoutIconColor}
              logoutTitleColor={logoutTitleColor}
            >
              <NavItem
                onClick={(
                  e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                ) => {
                  onNavItemClick(e, item, hasUserLevelAccessForFeature);
                }}
                className={`${className} ${isNavItemDisabled ? 'disabled' : ''}`}
                key={index}
                to={isNavItemDisabled || !path ? index.toString() : path}
                exact={exact}
                disabled={isNavItemDisabled}
              >
                {isDisabled || !hasUserLevelAccessForFeature ? (
                  <DisabledItem
                    item={item}
                    hasUserLevelAccessForFeature={hasUserLevelAccessForFeature}
                  />
                ) : (
                  <EnabledItem item={item} />
                )}
              </NavItem>
            </IconWrap>
          );
        })}

        {checkIfFreemium(userData) && (
          <>
            {inviteFriends && (
              <InviteFriendsModal
                handleModalClose={() => setModal(false)}
                onDismissHandler={() => null}
                onMaybeLaterHandler={() => null}
              />
            )}
            <Button
              style={{
                margin: '56px auto 0 auto',
              }}
              text={t('Invite Friends')}
              variant='white'
              icon={
                <IoMdPeople color={theme.palette.covideoBlue100} size={20} />
              }
              onClick={() => {
                setModal(true);
              }}
            />
          </>
        )}
      </ItemsWrapper>
    </Container>
  );
};
